// The deploy wizard's "Expected delivery" box for an external forecast model: between the stored
// ProcessedModelSchema.deliveryExpectation and the form (a ScheduleEditor value plus an allowance). Kept apart
// from deliveryExpectation.ts, which erodeapifunction also bundles.

import {
  ScheduleConfig,
  defaultScheduleConfig,
  runScheduleToScheduleConfig,
  scheduleConfigToRunSchedule,
  summarizeScheduleConfig,
} from "../components/Schedule/scheduleConfig";
import {
  DEFAULT_ALLOWANCE_MINUTES,
  DeliveryExpectation,
  DeliveryExpectationErrorCode,
  describeAllowance,
  normalizeDeliveryExpectation,
} from "./deliveryExpectation";

export type AllowanceUnit = "min" | "h";

// The shared rule's messages name API fields; the box says them its own way
const WIZARD_ERRORS: Record<DeliveryExpectationErrorCode, string> = {
  not_object: "Choose when forecasts are issued",
  invalid_cron: "That isn't a valid cron (minute hour day month weekday year)",
  too_frequent: "Forecasts can be expected at most once an hour",
  invalid_timezone: "Choose a timezone",
  invalid_allowance: "The allowance must be from 0 to 72 hours",
};

export interface DeliveryForm {
  enabled: boolean;
  schedule: ScheduleConfig;
  allowanceValue: string;
  allowanceUnit: AllowanceUnit;
}

/** The form for a model with this expectation (or a fresh, switched-off one). */
export function deliveryFormFrom(expectation: DeliveryExpectation | null): DeliveryForm {
  const minutes = expectation?.allowanceMinutes ?? DEFAULT_ALLOWANCE_MINUTES;
  const inHours = minutes >= 60 && minutes % 30 === 0;
  return {
    enabled: Boolean(expectation),
    schedule: expectation
      ? runScheduleToScheduleConfig({
          frequency: expectation.frequency || "CUSTOM",
          cronExpression: expectation.cronExpression,
          timezone: expectation.timezone,
        })
      : { ...defaultScheduleConfig(), timezone: "UTC" },
    allowanceValue: inHours ? String(minutes / 60) : String(minutes),
    allowanceUnit: inHours ? "h" : "min",
  };
}

/**
 * The expectation to save, or the error to show beside the box. Switched off saves null. `previous` keeps the
 * grading start when only the allowance changed.
 */
export function expectationFromDeliveryForm(
  form: DeliveryForm,
  previous: DeliveryExpectation | null,
  now: Date = new Date()
): { value: DeliveryExpectation | null; error?: undefined } | { error: string; value?: undefined } {
  if (!form.enabled) return { value: null };
  const amount = Number(form.allowanceValue);
  if (form.allowanceValue.trim() === "" || !Number.isFinite(amount) || amount < 0) {
    return { error: "Enter how long after the issue time a forecast may arrive (0 or more)" };
  }
  const run = scheduleConfigToRunSchedule(form.schedule);
  if (!run.cronExpression) return { error: "Choose when forecasts are issued" };
  const result = normalizeDeliveryExpectation(
    {
      cronExpression: run.cronExpression,
      timezone: run.timezone,
      allowanceMinutes: form.allowanceUnit === "h" ? amount * 60 : amount,
      frequency: run.frequency,
    },
    { previous, now }
  );
  if (result.error !== undefined) return { error: WIZARD_ERRORS[result.code] };
  return { value: result.value };
}

/** One line for the wizard's review step. */
export function summarizeDeliveryForm(form: DeliveryForm): string {
  if (!form.enabled) return "Not tracked";
  const amount = Number(form.allowanceValue);
  const minutes = Number.isFinite(amount) ? (form.allowanceUnit === "h" ? amount * 60 : amount) : DEFAULT_ALLOWANCE_MINUTES;
  return `Issued ${summarizeScheduleConfig(form.schedule).replace(/^Custom cron: /, "on cron ")} (${form.schedule.timezone}), delivered ${describeAllowance(Math.round(minutes))}`;
}
